import {Button} from '@/components/ui/button/Button'
import {ComboboxInput, ComboboxItem, ComboboxPopover} from '@/components/ui/combobox-atoms/ComboboxAtoms'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Trash01Icon} from '@/components/ui/icon/Icon'
import {InputHelpText} from '@/components/ui/input-help-text/InputHelpText'
import {InputStatusIcon} from '@/components/ui/input-status-icon/InputStatusIcon'
import {SelectComboboxList} from '@/components/ui/select-atoms/SelectAtoms'
import {TKey, useTranslation} from '@/translations/i18n'
import {ObjectValues} from '@/utilities/helpers'
import {ComboboxProvider, useComboboxStore} from '@ariakit/react'
import {useMemo, useState} from 'react'
import {UseFieldArrayRemove} from 'react-hook-form'

interface ContactComboboxLabelProps<T extends string = string> {
    index: number
    value: T
    onChange: (value: T) => void
    remove?: UseFieldArrayRemove | null
    optionsMap: Record<T, TKey>
    errorMessage?: string
}

export const ContactComboboxLabel = ({
    index,
    value,
    onChange,
    remove,
    optionsMap,
    errorMessage
}: ContactComboboxLabelProps) => {
    const {t} = useTranslation()
    const [searchLabel, setSearchLabel] = useState('')
    const combobox = useComboboxStore({
        defaultValue: value,
        setValue: setSearchLabel,
        setSelectedValue: value => onChange(value as string)
    })

    const matchedLabelsList = useMemo(
        () =>
            ObjectValues(optionsMap).filter(label => t(label).toLowerCase().trim().includes(searchLabel.toLowerCase())),
        [searchLabel]
    )

    return (
        <ComboboxProvider store={combobox}>
            <Flexbox direction="column" gap={1.5} fullWidth>
                <Flexbox gap={2} align="center" fullWidth>
                    <ComboboxInput
                        placeholder={t('commons:label_placeholder')}
                        onBlur={() => onChange(searchLabel)}
                        onFocusVisible={() => combobox.setOpen(true)}
                        errorMessage={errorMessage}
                    />
                    {remove && (
                        <Button variant="ghost" onClick={() => remove(index)}>
                            <Trash01Icon />
                        </Button>
                    )}
                </Flexbox>
                {errorMessage && (
                    <Flexbox align="center" gap={1.5}>
                        <InputStatusIcon hasError />
                        <InputHelpText error={errorMessage} />
                    </Flexbox>
                )}
            </Flexbox>
            {matchedLabelsList?.length > 0 && (
                <ComboboxPopover style={{gridTemplateRows: 'minmax(0, 1fr)'}}>
                    <SelectComboboxList>
                        {matchedLabelsList.map(label => (
                            <ComboboxItem key={label} value={t(label)}>
                                {t(label)}
                            </ComboboxItem>
                        ))}
                    </SelectComboboxList>
                </ComboboxPopover>
            )}
        </ComboboxProvider>
    )
}
